import * as React from 'react';
import { useUser } from 'lib/auth';
import { Link } from 'components/ui/link';
import {
    Bot,
    Cog,
    Database,
    Frame,
    Rocket,
    Settings2,
    TerminalSquare,
    Scale,
    Hammer,
    MessageCircle,
    Key,
    Telescope,
    BookOpen,
    Lock,
    ScanText,
    File,
    FileCheck,
    ChartLine,
    Tags,
    TrendingDown,
    BarChart4,
} from 'lucide-react';
import logo from 'assets/logo.svg';
import logoDark from 'assets/logo-dark.svg';

import { NavMain } from 'components/layouts/dashboard-layout/nav-main';
import { NavProjects } from 'components/layouts/dashboard-layout/nav-projects';
import { NavSecondary } from 'components/layouts/dashboard-layout/nav-secondary';
import { NavUser } from 'components/layouts/dashboard-layout/nav-user';
import {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarHeader,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
} from 'components/ui/sidebar';
import { User } from 'types/api';
import { useTheme } from 'components/styles/theme-provider';

const data = {
    navMain: [
        {
            title: 'Projects',
            url: '/app/projects',
            icon: Bot,
            isActive: true,
        },
        {
            title: 'Datasets',
            url: '/app/datasets',
            icon: Database,
        },
        {
            title: 'Graders',
            url: '/app/graders',
            icon: Scale,
        },
        {
            title: 'Tools',
            url: '/app/tools',
            icon: Hammer,
        },
        {
            title: 'Deployments',
            url: '/app/deployments',
            icon: Rocket,
        },
        {
            title: 'Telemetry',
            url: '/app/telemetry',
            icon: Telescope,
        },
        {
            title: 'Sala Chat',
            url: '/app/sala-chat',
            icon: MessageCircle,
            condition: (user: User | undefined) =>
                user?.organization.id ===
                'f9d51260-284c-43f6-b02b-0f0436f65152',
        },
        // {
        //     title: 'AIA',
        //     url: '/app/sala-aia',
        //     icon: File,
        //     condition: (user: User | undefined) =>
        //         user?.organization.id ===
        //         'f9d51260-284c-43f6-b02b-0f0436f65152',
        // },
        {
            title: 'Medicard Claims',
            url: '/app/medicard-claims',
            icon: File,
            condition: (user: User | undefined) =>
                user?.organization.id ===
                'f9d51260-284c-43f6-b02b-0f0436f65152',
        },
        {
            title: 'Insurance Analytics',
            url: '/app/insurance-analytics',
            icon: ChartLine,
            condition: (user: User | undefined) =>
                [
                    'f9d51260-284c-43f6-b02b-0f0436f65152',
                    '0a42e458-9485-4453-9d7f-7df190f798f8',
                ].includes(user?.organization.id || ''),
        },
        {
            title: 'Maxicare Analytics Chat',
            url: '/app/maxicare-analytics-chat',
            icon: MessageCircle,
            condition: (user: User | undefined) =>
                user?.organization.id ===
                'f9d51260-284c-43f6-b02b-0f0436f65152',
        },
        {
            title: 'Maxicare IP Claim Insights',
            url: '/app/maxicare-ip-claim-insights',
            icon: TrendingDown,
            condition: (user: User | undefined) =>
                user?.organization.id ===
                'f9d51260-284c-43f6-b02b-0f0436f65152',
        },
        {
            title: 'Entity Extraction',
            url: '/app/entity-extraction',
            icon: Tags,
            condition: (user: User | undefined) =>
                [
                    '9309693d-c537-4cbc-9ca7-68ea2aa396d6',
                    '0a42e458-9485-4453-9d7f-7df190f798f8',
                    'f9d51260-284c-43f6-b02b-0f0436f65152',
                ].includes(user?.organization?.id || ''),
        },
        {
            title: 'Aster Analytics Demo',
            url: '/app/aster-analytics-demo',
            icon: BarChart4,
            condition: (user: User | undefined) =>
                [
                    '9309693d-c537-4cbc-9ca7-68ea2aa396d6',
                    '0a42e458-9485-4453-9d7f-7df190f798f8',
                ].includes(user?.organization?.id || ''),
        },
        {
            title: 'API Keys',
            url: '/app/api-keys',
            icon: Key,
        },
        {
            title: 'Secrets',
            url: '/app/secrets',
            icon: Lock,
        },
        {
            title: 'OCR',
            url: '/app/ocr',
            icon: ScanText,
        },
        {
            title: 'Documentation',
            icon: BookOpen,
            url: '/app/documentation',
            items: [
                {
                    title: 'Introduction',
                    url: '/app/documentation/introduction',
                },
                {
                    title: 'Projects Guide',
                    url: '/app/documentation/projects',
                },
                {
                    title: 'Datasets Guide',
                    url: '/app/documentation/datasets',
                },
                {
                    title: 'Graders Guide',
                    url: '/app/documentation/graders',
                },
                {
                    title: 'Tools Guide',
                    url: '/app/documentation/tools',
                },
                {
                    title: 'Implementations Guide',
                    url: '/app/documentation/implementations',
                },
                {
                    title: 'Deployments Guide',
                    url: '/app/documentation/deployments',
                },
                {
                    title: 'Telemetry Guide',
                    url: '/app/documentation/telemetry',
                },
                {
                    title: 'Secrets Guide',
                    url: '/app/documentation/secrets',
                },
                {
                    title: 'OCR Guide',
                    url: '/app/documentation/ocr',
                },
                {
                    title: 'Data Quality Review Guide',
                    url: '/app/documentation/data-quality-review',
                },
                {
                    title: 'API Documentation',
                    url: '/app/documentation/api',
                },
                {
                    title: 'Model Pricing',
                    url: '/app/documentation/model-pricing',
                },
            ],
        },
    ],
    navSecondary: [
        {
            title: 'Settings',
            url: '/app/settings',
            icon: Cog,
        },
        // {
        //     title: 'Support',
        //     url: '/app#',
        //     icon: LifeBuoy,
        // },
        // {
        //     title: 'Feedback',
        //     url: '/app#',
        //     icon: Send,
        // },
    ],
    projects: [
        {
            name: 'Overview',
            url: '/app/projects/:projectId',
            icon: Frame,
        },
        {
            name: 'Configuration',
            url: '/app/projects/:projectId/configuration',
            icon: Settings2,
        },
        {
            name: 'Data Quality Review',
            url: '/app/projects/:projectId/data-quality-review',
            icon: FileCheck,
        },
        {
            name: 'Implementations',
            url: '/app/projects/:projectId/implementations',
            icon: TerminalSquare,
        },
    ],
};

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
    const { data: user } = useUser();
    const { isDark } = useTheme();
    return (
        <Sidebar variant="inset" {...props}>
            <SidebarHeader>
                <SidebarMenu>
                    <SidebarMenuItem>
                        <SidebarMenuButton size="lg" asChild>
                            <Link to="/app">
                                <div className="flex aspect-square items-center justify-center text-sidebar-primary-foreground">
                                    <img
                                        src={isDark ? logoDark : logo}
                                        alt="Atomix"
                                        className="h-8 w-8"
                                    />
                                </div>
                                <div className="grid flex-1 text-left text-sm leading-tight">
                                    <span className="truncate font-semibold">
                                        Atomix
                                    </span>
                                </div>
                            </Link>
                        </SidebarMenuButton>
                    </SidebarMenuItem>
                </SidebarMenu>
            </SidebarHeader>
            <SidebarContent>
                <NavMain items={data.navMain} user={user} />
                <NavProjects projects={data.projects} />
                <NavSecondary items={data.navSecondary} className="mt-auto" />
            </SidebarContent>
            <SidebarFooter>
                <NavUser />
            </SidebarFooter>
        </Sidebar>
    );
}
